import { AxiosInstance } from 'axios'
import { snackbarProviderRef } from 'common/components/NotistackProvider'
import delay from 'common/utils/delay'
import { getLocalStorage, setLocalStorage } from 'common/utils/localstorage'
import { renewAccessToken } from 'modules/auth/api'
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from 'modules/auth/constants'

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)')
  )
  return match ? decodeURIComponent(match[1]) : null
}

// --- token bootstrap helpers (Flutter WebView friendly) ---
declare global {
  interface Window {
    __SET_TOKENS__?: (tokens: { accessToken: string; refreshToken: string }) => void
    flutter_inappwebview?: {
      callHandler?: (handlerName: string, ...args: unknown[]) => Promise<unknown>
    }
  }
}

function setTokenEverywhere(accToken: string, refToken: string) {
  try {
    // ห้ามเขียนค่าว่างทับ session ที่ใช้ได้อยู่ — แอปเวอร์ชันเก่า inject token
    // จาก state ที่ยัง seed ไม่เสร็จ แล้วหน้าเว็บกลายเป็นไม่มี token (NK-230)
    if (accToken) setLocalStorage(ACCESS_TOKEN_KEY, accToken)
    if (refToken) setLocalStorage(REFRESH_TOKEN_KEY, refToken)
  } catch {}
}

if (typeof window !== 'undefined') {
  window.__SET_TOKENS__ = ({ accessToken, refreshToken }) =>
    setTokenEverywhere(accessToken, refreshToken)
  window.addEventListener('message', (evt: MessageEvent) => {
    const data: any = evt?.data
    if (!data) return

    if (
      (data.type === 'SET_TOKENS' || data.type === 'AUTH_TOKENS') &&
      typeof data.accessToken === 'string' &&
      typeof data.refreshToken === 'string'
    ) {
      setTokenEverywhere(data.accessToken, data.refreshToken)
    }
  })
}

function getTokenFromAllSources(): string | null {
  const fromLs = getLocalStorage(ACCESS_TOKEN_KEY)
  if (fromLs) return fromLs as string
  const fromCookie = readCookie(ACCESS_TOKEN_KEY)
  if (fromCookie) {
    setLocalStorage(ACCESS_TOKEN_KEY, fromCookie)
    return fromCookie
  }
  return null
}
// --- mobile host bridge -------------------------------------------------
// เมื่อหน้านี้ถูกเปิดใน WebView ของแอป มือถือเป็นเจ้าของ token ทั้งหมด
// (refresh token ใช้ได้ครั้งเดียว) หน้าเว็บจึงต้องขอ access token ใหม่จาก host
// แทนการเรียก /renew/access-token เอง ไม่งั้น token ของสองฝั่งจะหลุดออกจากกัน
const HOST_ACCESS_TOKEN_HANDLER = 'nkAuthGetAccessToken'

function isHostedInAppWebview(): boolean {
  return (
    typeof window !== 'undefined' && typeof window.flutter_inappwebview?.callHandler === 'function'
  )
}

let hostTokenPromise: Promise<string | null> | null = null

async function askHostForAccessToken(usedAccessToken: string | null): Promise<string | null> {
  try {
    const result = await window.flutter_inappwebview!.callHandler!(HOST_ACCESS_TOKEN_HANDLER, {
      usedAccessToken,
    })
    const accessToken =
      typeof result === 'string'
        ? result
        : (result as { accessToken?: unknown } | null)?.accessToken
    if (typeof accessToken === 'string' && accessToken) {
      setLocalStorage(ACCESS_TOKEN_KEY, accessToken)
      return accessToken
    }
  } catch {
    // host ไม่ตอบ / ไม่ได้ลงทะเบียน handler — ปล่อยให้ request fail ไปตามปกติ
  }
  return null
}

function requestAccessTokenFromHost(usedAccessToken: string | null): Promise<string | null> {
  if (!hostTokenPromise) {
    hostTokenPromise = askHostForAccessToken(usedAccessToken).finally(() => {
      hostTokenPromise = null
    })
  }
  return hostTokenPromise
}

// --- end helpers ---

let isRefreshing = false
let refreshPromise: Promise<any> | null = null

export default function applyInterceptors(instance: AxiosInstance) {
  instance.interceptors.request.use((config) => {
    const token = getTokenFromAllSources()
    // Ensure headers object exists
    config.headers = config.headers || ({} as any)
    if (token) {
      ;(config.headers as any).Authorization = `Bearer ${token}`
    }
    return config
  })

  instance.interceptors.response.use(
    (res) => res,
    async (err) => {
      const { response, config } = err
      if (response) {
        const status = response.status

        if (status === 401 && !config._retry) {
          await delay(200)
          config._retry = true

          if (isHostedInAppWebview()) {
            const usedAccessToken = (config.headers?.Authorization as string | undefined)?.replace(
              /^Bearer /,
              ''
            )
            const hostAccessToken = await requestAccessTokenFromHost(usedAccessToken ?? null)
            if (hostAccessToken) {
              config.headers = config.headers || {}
              config.headers.Authorization = `Bearer ${hostAccessToken}`
              return instance(config)
            }
            // host จัดการ session เอง (จะ refresh หรือเตะออก) — ห้ามล้าง storage
            // หรือเด้งไป /auth/login เพราะใน WebView จะกลายเป็นหน้าขาว
            return Promise.reject(err)
          }

          if (!isRefreshing) {
            isRefreshing = true

            refreshPromise = (async () => {
              try {
                await delay(200)
                const rf = getLocalStorage(REFRESH_TOKEN_KEY)
                const res = await renewAccessToken(rf)
                const {
                  data: { accessToken, refreshToken },
                } = res.data
                setLocalStorage(ACCESS_TOKEN_KEY, accessToken)
                setLocalStorage(REFRESH_TOKEN_KEY, refreshToken)
                setTokenEverywhere(accessToken, refreshToken)
                // Ensure the retried request has the header
                config.headers = config.headers || {}
                config.headers.Authorization = `Bearer ${accessToken}`
                return accessToken
              } catch (error) {
                localStorage.clear()
                window.location.replace('/auth/login')
                throw error
              } finally {
                isRefreshing = false
                refreshPromise = null
              }
            })()
          }

          try {
            const newAccessToken = await refreshPromise
            config.headers.Authorization = `Bearer ${newAccessToken}`
            return instance(config)
          } catch (error) {
            return Promise.reject(error)
          }
        }

        const skipStatuses = [400, 401, 409, 403]
        if (!skipStatuses.includes(status)) {
          const errorMessage = 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'
          snackbarProviderRef.current?.enqueueSnackbar(errorMessage, {
            variant: 'error',
          })
        }

        if (status === 403) {
          const errorMessage = 'ขออภัย คุณไม่มีสิทธิ์การเข้าถึง'
          snackbarProviderRef.current?.enqueueSnackbar(errorMessage, {
            variant: 'default',
          })
        }
      }

      return Promise.reject(err)
    }
  )
}
